import {Parameter} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import Immutable from "../../shared/immutable/Immutable";
import {PortalDocumentInterface} from "./PortalDocument";
import SortHelper from "../../shared/helper/SortHelper";
import React, {ReactNode} from "react";

export interface DocumentSortingGlobals {
	documentSorting: Immutable<DocumentSortingInterface>;
}

export function initDocumentSortingGlobals(): DocumentSortingGlobals {
	return {
		documentSorting: Immutable.from(new DocumentSorting())
	}
}

export enum SortType {
	NONE,
	NAME,
	ARCHIVE,
	SIGNATURE,
	PASSWORD,
	ATTACHMENT,
	SIZE
}

export enum SortDirection {
	NONE,
	ASC,
	DESC
}

export interface DocumentSortingInterface {
	sortBy: SortType;
	sortDirection: SortDirection;
}

export default class DocumentSorting implements DocumentSortingInterface, Parameter {
	public sortBy: SortType;
	public sortDirection: SortDirection;

	constructor(data?: any) {
		this.sortBy = FunctionHelper.exists(data?.sortBy) ? data.sortBy : SortType.NONE;
		this.sortDirection = FunctionHelper.exists(data?.sortDirection) ? data.sortDirection : SortDirection.NONE;
	}

	public setSorting(type: SortType): void {
		if (this.sortDirection === SortDirection.NONE) {
			this.sortDirection = SortDirection.ASC;
		}

		if (this.sortBy === type) {
			switch (this.sortDirection) {
				case SortDirection.ASC:
					this.sortDirection = SortDirection.DESC;
					break;
				case SortDirection.DESC:
					this.sortDirection = SortDirection.NONE;
					this.sortBy = SortType.NONE;
			}
		} else {
			this.sortBy = type;
		}
	}

	public sortItems(listItems: Array<PortalDocumentInterface>): Array<PortalDocumentInterface> {
		let result: Array<PortalDocumentInterface> = listItems;

		if (this.sortDirection === SortDirection.NONE) {
			return result;
		}

		result = result.sort((a: PortalDocumentInterface, b: PortalDocumentInterface) => {
			switch (this.sortBy) {
				case SortType.SIZE:
					return SortHelper.numeric(a.size, b.size);
				case SortType.ARCHIVE:
					return SortHelper.alphanum(a.archiveType, b.archiveType);
				case SortType.ATTACHMENT:
					return SortHelper.numeric(a.attachments, b.attachments);
				case SortType.PASSWORD:
					let aValue: number = a.hasSignatureProtection ? 2 : a.hasPasswordProtection ? 1 : 0;
					let bValue: number = b.hasSignatureProtection ? 2 : b.hasPasswordProtection ? 1 : 0;

					return SortHelper.numeric(aValue, bValue);
				case SortType.SIGNATURE:
					return SortHelper.numeric(a.signatures, b.signatures);
				default:
					return SortHelper.alphanum(a.name, b.name);
			}
		});

		if (this.sortDirection === SortDirection.DESC) {
			result = result.reverse();
		}

		return result;
	}

	public renderSortIcon(type: SortType): ReactNode {
		if (type !== this.sortBy) {
			return <span className="icon icon-sort-none"/>;
		}

		switch (this.sortDirection) {
			case SortDirection.NONE:
				return <span className="icon icon-sort-none"/>;
			case SortDirection.ASC:
				return <span className="icon icon-sort-asc"/>;
			case SortDirection.DESC:
				return <span className="icon icon-sort-desc"/>;
		}
	}

	public static fromJson(data: any): DocumentSorting {
		if (!FunctionHelper.exists(data)) {
			return data;
		}

		return new DocumentSorting(data);
	}

	public toJson(): any {
		return {
			'sortBy': this.sortBy,
			'sortDirection': this.sortDirection
		};
	}

	public clone(): DocumentSorting {
		return DocumentSorting.fromJson(this.toJson());
	}
}